import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '@/context/StoreContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Linkedin, Instagram, Facebook, Mail } from 'lucide-react';

const FOOT_BG = '/images/categories/vineyard.jpg';
export const CONTACT_EMAIL = 'contact@nicewine.com';
export const COMPANY_ADDRESS = '15 rue Claude Terrasse, 75016 Paris, France';

export const Footer = () => {
  const { t, customer } = useStore();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await api.newsletter(email); setDone(true); toast.success(t.footer.subscribed); setEmail(''); }
    catch { toast.error(t.common.error); }
  };

  return (
    <footer className="relative mt-0">
      <div className="relative overflow-hidden nw-vignette">
        <img src={FOOT_BG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-nw-black/70" />
        <div className="relative nw-container py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="font-serif text-nw-gold text-2xl tracking-[0.25em]">NICEWINE</div>
              <p className="mt-4 max-w-md text-nw-warmgrey text-sm leading-relaxed">{t.footer.newsletterSub}</p>
            </div>
            <div>
              <div className="nw-eyebrow mb-3">{t.footer.newsletter}</div>
              <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-md">
                <input data-testid="footer-newsletter-email-input" type="email" required value={email} aria-label={t.footer.emailPlaceholder}
                  onChange={(e) => setEmail(e.target.value)} placeholder={t.footer.emailPlaceholder}
                  className="flex-1 bg-transparent border border-nw-gold/30 rounded-[3px] px-4 h-11 text-nw-ivory placeholder:text-nw-warmgrey/60 focus:outline-none focus:border-nw-gold text-sm" />
                <button data-testid="footer-newsletter-submit-button" type="submit" className="nw-btn-gold">{t.footer.subscribe}</button>
              </form>
              {done && <p className="mt-2 text-nw-gold text-xs tracking-wide">{t.footer.subscribed}</p>}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-nw-black border-t border-nw-gold/15">
        <div className="nw-container py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <nav aria-label="Footer" className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] tracking-luxe uppercase text-nw-warmgrey">
            <Link to="/contact" className="nw-link">{t.footer.faq}</Link>
            <Link to="/import-export" className="nw-link">{t.footer.delivery}</Link>
            <Link to="/cgv#retractation" className="nw-link">{t.footer.returns}</Link>
            <Link to="/cgv" data-testid="footer-terms-link" className="nw-link">{t.footer.terms}</Link>
            <Link to={customer ? '/account' : '/account/login'} data-testid="footer-account-link" className="nw-link">{t.footer.account}</Link>
            <Link to="/contact" className="nw-link">{t.footer.contact}</Link>
            <Link to="/credits" data-testid="footer-credits-link" className="nw-link">{t.footer.credits}</Link>
          </nav>
          <div className="flex items-center gap-4 text-nw-warmgrey">
            <a href="https://www.linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="hover:text-nw-gold transition-colors"><Linkedin size={16} /></a>
            <a href="https://www.instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-nw-gold transition-colors"><Instagram size={16} /></a>
            <a href="https://www.facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-nw-gold transition-colors"><Facebook size={16} /></a>
          </div>
          <div data-testid="footer-copyright" className="text-[11px] text-nw-warmgrey/70 tracking-wide">© 2026 NICEWINE. {t.footer.rights}</div>
        </div>
        <div className="nw-container pb-6 text-[11px] text-nw-warmgrey/60 tracking-wide flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>{COMPANY_ADDRESS}</span>
          <span aria-hidden="true">·</span>
          <a data-testid="footer-contact-email" href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-1.5 hover:text-nw-gold transition-colors"><Mail size={12} /> {CONTACT_EMAIL}</a>
        </div>
      </div>
    </footer>
  );
};
